<script setup lang="ts">
import type { ThemeSettings } from '~/stores/auth'
import { hexToRgb } from '~/composables/useLayoutStyles'
import { useCardHover } from '~/composables/useCardHover'
import { getBoostBadgeLevel, getDominantColor, getNameplateBackground } from '~/utils/discordProfile'
import type { DiscordDisplayNameStyles } from '~/utils/discordProfile'

interface DiscordProfileData {
  discordUserId: string
  username: string
  globalName: string | null
  avatarUrl: string
  avatarStaticUrl: string
  bannerUrl: string | null
  bannerStaticUrl: string | null
  bannerColor: string | null
  avatarDecoration: { url: string, staticUrl: string } | null
  nameplate: { videoUrl: string, staticUrl: string, palette: string } | null
  displayNameStyles: DiscordDisplayNameStyles | null
  serverTag: { tag: string, badgeUrl: string | null } | null
  badges: string[]
  hasNitro: boolean
  boostingSince: string | null
  createdAt: string
}

interface BadgeInfo {
  src: string
  label: string
}

// Labels match Discord's badge tooltips
const BADGE_MAP: Record<string, BadgeInfo> = {
  discord_employee: { src: '/badges/discord/discordstaff.svg', label: 'Discord Staff' },
  partnered_server_owner: { src: '/badges/discord/discordpartner.svg', label: 'Partnered Server Owner' },
  certified_moderator: { src: '/badges/discord/discordmod.svg', label: 'Moderator Programs Alumni' },
  hypesquad_events: { src: '/badges/discord/hypesquadevents.svg', label: 'HypeSquad Events' },
  hypesquad_bravery: { src: '/badges/discord/hypesquadbravery.svg', label: 'HypeSquad Bravery' },
  hypesquad_brilliance: { src: '/badges/discord/hypesquadbrilliance.svg', label: 'HypeSquad Brilliance' },
  hypesquad_balance: { src: '/badges/discord/hypesquadbalance.svg', label: 'HypeSquad Balance' },
  bug_hunter_1: { src: '/badges/discord/discordbughunter1.svg', label: 'Discord Bug Hunter' },
  bug_hunter_2: { src: '/badges/discord/discordbughunter2.svg', label: 'Discord Bug Hunter' },
  active_developer: { src: '/badges/discord/activedeveloper.svg', label: 'Active Developer' },
  verified_developer: { src: '/badges/discord/discordbotdev.svg', label: 'Early Verified Bot Developer' },
  early_supporter: { src: '/badges/discord/discordearlysupporter.svg', label: 'Early Supporter' }
}

const NITRO_BADGE: BadgeInfo = { src: '/badges/discord/discordnitro.svg', label: 'Nitro' }

// Avatar is 80px with its center on the banner's bottom edge (16px padding + 40px)
// and a 6px ring cut out of the banner, like Discord's profile popout
const BANNER_MASK = 'radial-gradient(circle at 56px 100%, transparent 46px, #000 46.5px)'

const props = defineProps<{
  characterId: string
  themeSettings: ThemeSettings
  cardStyle?: Record<string, string>
}>()

const skinPreview = inject<boolean>('skinPreview', false)
const { onCardMouseMove, onCardMouseLeave } = useCardHover(toRef(props, 'themeSettings'))

const profile = ref<DiscordProfileData | null>(null)
const loaded = ref(false)
const avatarColor = ref<string | null>(null)

onMounted(async () => {
  if (skinPreview) {
    loaded.value = true
    return
  }
  try {
    profile.value = await $fetch<DiscordProfileData>(`/api/discord/member/${props.characterId}`)
  } catch {
    // Silent fail - component won't render
  } finally {
    loaded.value = true
  }

  // Discord paints the banner in the avatar's main color when there is no banner or banner color
  if (profile.value && !profile.value.bannerUrl && !profile.value.bannerColor) {
    avatarColor.value = await getDominantColor(profile.value.avatarStaticUrl) ?? '#5865F2'
  }
})

// Effects start when the card scrolls into view and replay on hover, like Discord.
// Hovering must not change reactive state: a re-render re-applies the card's
// inline entrance animation, which made the card fade in again.
const cardRef = ref<HTMLElement | null>(null)
const displayNameRef = ref<{ replay: () => void } | null>(null)
const inView = ref(false)
const reducedMotion = usePreferredReducedMotion()

const { stop: stopObserver } = useIntersectionObserver(cardRef, ([entry]) => {
  if (!entry?.isIntersecting) return
  inView.value = true
  stopObserver()
})

function onMouseEnter() {
  if (inView.value) displayNameRef.value?.replay()
}

// The nameplate fills the card body. Like Discord's member list, its art is
// dimmed on the left where the text sits, and the tint brightens on hover
// through CSS (see the nameplate layer classes).
const NAMEPLATE_MASK = 'linear-gradient(to right, rgba(0, 0, 0, 0.3) 30%, #000 75%)'
// Keeps the small grey text readable on bright nameplate art
const NAMEPLATE_TEXT_SHADOW = '[text-shadow:0_1px_3px_rgb(0_0_0/0.7)]'

const nameplateLayerStyle = computed(() => {
  const nameplate = profile.value?.nameplate
  if (!nameplate) return undefined
  return {
    '--nameplate-bg': getNameplateBackground(nameplate.palette) ?? 'none',
    '--nameplate-bg-hover': getNameplateBackground(nameplate.palette, true) ?? 'none',
    'maskImage': NAMEPLATE_MASK,
    'WebkitMaskImage': NAMEPLATE_MASK
  }
})

const showNameplateVideo = computed(() => inView.value && reducedMotion.value !== 'reduce')

const displayName = computed(() => profile.value?.globalName || profile.value?.username || '')

const cardBg = computed(() => {
  const rgb = hexToRgb(props.themeSettings.cardColor)
  return `rgba(${rgb.join(',')}, ${Math.min(props.themeSettings.cardOpacity / 100, 0.6)})`
})

// Neutral until the avatar color is known, so the banner does not flash another color
const bannerColor = computed(() => profile.value?.bannerColor ?? avatarColor.value ?? 'rgba(255, 255, 255, 0.06)')

const profileUrl = computed(() => {
  if (!profile.value) return '#'
  return `https://discord.com/users/${profile.value.discordUserId}`
})

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}

// Public flag badges, then Nitro and Server Booster, in Discord's order
const badges = computed<BadgeInfo[]>(() => {
  const p = profile.value
  if (!p) return []
  const list = p.badges
    .map(badge => BADGE_MAP[badge])
    .filter((badge): badge is BadgeInfo => !!badge)
  if (p.hasNitro) list.push(NITRO_BADGE)
  if (p.boostingSince) {
    list.push({
      src: `/badges/discord/boosts/discordboost${getBoostBadgeLevel(p.boostingSince)}.svg`,
      label: `Server boosting since ${formatDate(p.boostingSince)}`
    })
  }
  return list
})
</script>

<template>
  <SkinPreviewPlaceholder
    v-if="skinPreview"
    label="Discord Embed"
    icon="i-simple-icons-discord"
    :card-style="cardStyle"
    :theme-settings="themeSettings"
  />
  <a
    v-else-if="loaded && profile"
    ref="cardRef"
    :href="profileUrl"
    target="_blank"
    rel="noopener noreferrer"
    class="block rounded-xl overflow-hidden transition-all duration-200 group cursor-pointer no-underline"
    :style="{
      background: cardBg,
      ...cardStyle
    }"
    @mouseenter="onMouseEnter"
    @mousemove="onCardMouseMove"
    @mouseleave="onCardMouseLeave"
  >
    <!-- Banner, with a ring cut out around the avatar -->
    <div
      class="w-full"
      :class="profile.bannerUrl ? 'aspect-17/6' : 'h-16'"
      :style="{ maskImage: BANNER_MASK, WebkitMaskImage: BANNER_MASK }"
    >
      <picture
        v-if="profile.bannerUrl"
        class="block size-full"
      >
        <source
          v-if="profile.bannerStaticUrl"
          :srcset="profile.bannerStaticUrl"
          media="(prefers-reduced-motion: reduce)"
        >
        <img
          :src="profile.bannerUrl"
          alt=""
          class="size-full object-cover"
          loading="lazy"
        >
      </picture>
      <div
        v-else
        class="size-full transition-colors duration-300"
        :style="{ backgroundColor: bannerColor }"
      />
    </div>

    <!-- Body, with the nameplate as its background like Discord's member list rows -->
    <div class="relative isolate flow-root px-4 pb-3">
      <div
        v-if="profile.nameplate"
        class="absolute inset-0 -z-10 overflow-hidden pointer-events-none [background:var(--nameplate-bg)] group-hover:[background:var(--nameplate-bg-hover)]"
        :style="nameplateLayerStyle"
        aria-hidden="true"
      >
        <video
          v-if="showNameplateVideo"
          :src="profile.nameplate.videoUrl"
          :poster="profile.nameplate.staticUrl"
          class="size-full object-cover object-right"
          autoplay
          muted
          loop
          playsinline
        />
        <img
          v-else
          :src="profile.nameplate.staticUrl"
          alt=""
          class="size-full object-cover object-right"
          loading="lazy"
        >
      </div>

      <!-- Avatar with decoration + badges -->
      <div class="flex items-start justify-between gap-3">
        <div class="relative -mt-10 size-20 shrink-0">
          <picture class="block size-20">
            <source
              :srcset="profile.avatarStaticUrl"
              media="(prefers-reduced-motion: reduce)"
            >
            <img
              :src="profile.avatarUrl"
              :alt="displayName || 'Discord user'"
              class="size-20 rounded-full object-cover"
              loading="lazy"
            >
          </picture>
          <picture v-if="profile.avatarDecoration">
            <source
              :srcset="profile.avatarDecoration.staticUrl"
              media="(prefers-reduced-motion: reduce)"
            >
            <img
              :src="profile.avatarDecoration.url"
              alt=""
              class="absolute -inset-2 size-24 max-w-none pointer-events-none"
              loading="lazy"
            >
          </picture>
        </div>

        <div
          v-if="badges.length > 0"
          class="mt-2.5 flex flex-wrap justify-end gap-0.5 rounded-lg bg-black/30 p-1"
        >
          <UTooltip
            v-for="badge in badges"
            :key="badge.src"
            :text="badge.label"
          >
            <img
              :src="badge.src"
              :alt="badge.label"
              class="size-5"
            >
          </UTooltip>
        </div>
      </div>

      <!-- Name block -->
      <div class="mt-2.5 min-w-0">
        <div class="flex w-fit max-w-full items-center gap-1.5">
          <DiscordDisplayName
            ref="displayNameRef"
            :name="displayName"
            :styles="profile.displayNameStyles"
            :animated="inView"
            class="text-xl font-bold leading-tight text-white"
          />
          <span
            v-if="profile.serverTag"
            class="inline-flex h-4.5 shrink-0 items-center gap-1 rounded bg-white/10 px-1 text-[11px] font-semibold leading-none text-white/80"
          >
            <img
              v-if="profile.serverTag.badgeUrl"
              :src="profile.serverTag.badgeUrl"
              alt=""
              class="size-3.5"
            >
            {{ profile.serverTag.tag }}
          </span>
        </div>
        <span
          class="block truncate text-sm leading-tight text-white/60"
          :class="{ [NAMEPLATE_TEXT_SHADOW]: profile.nameplate }"
        >
          {{ profile.username }}
        </span>
      </div>

      <!-- Footer -->
      <div class="mt-3 flex items-center justify-between gap-2">
        <span
          class="text-[11px] text-white/40"
          :class="{ [NAMEPLATE_TEXT_SHADOW]: profile.nameplate }"
        >
          Member since {{ formatDate(profile.createdAt) }}
        </span>
        <UIcon
          name="i-simple-icons-discord"
          class="size-3.5 shrink-0 text-white/15 group-hover:text-[#5865F2] transition-colors"
        />
      </div>
    </div>
  </a>
</template>
